import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { saveBlob } from '@/features/admin-v2/download';
import { api } from '@/lib/api';
import { clinicDate } from '@/lib/clinic-time';

export type PanelClaimStatus =
  | 'draft'
  | 'submitted'
  | 'queried'
  | 'approved'
  | 'paid'
  | 'rejected'
  | 'cancelled';

export interface PatientPanelClaim {
  id: string;
  claimReference: string;
  patientId: string;
  panelId: string;
  panelName: string;
  branchName: string;
  visitDate: string;
  claimedAmountSen: number;
  approvedAmountSen: number | null;
  patientAmountSen: number;
  status: PanelClaimStatus;
}

/** Claim fields safe for patient-facing and front-desk record views. */
export type PatientPanelClaimSummary = Omit<
  PatientPanelClaim,
  'approvedAmountSen'
>;

export type PanelBalanceStatus = 'within_limit' | 'warning' | 'exceeded';

export interface PatientPanelBalance {
  patientId: string;
  panelId: string;
  panelName: string;
  periodLabel: string;
  limitSen: number | null;
  usedSen: number;
  remainingSen: number | null;
  warningThresholdPercent: number;
  status: PanelBalanceStatus;
}

export type PanelOverrideType = 'panel_approval' | 'guarantee_letter';

export interface PatientPanelOverride {
  id: string;
  patientId: string;
  panelId: string;
  panelName: string;
  type: PanelOverrideType;
  reference: string;
  approvedAmountSen: number;
  reason: string;
  effectiveAt: string;
  expiresAt: string | null;
  active: boolean;
  recordedByName: string;
}

/** Override fields safe for roles that do not review claim decisions. */
export type PatientPanelOverrideSummary = Omit<
  PatientPanelOverride,
  'approvedAmountSen' | 'reason' | 'recordedByName'
>;

export interface FinancePatientPanelResult {
  patientId: string;
  patientName: string;
  identityLabel: string;
  panels: string[];
  claimCount: number;
  totalClaimedSen: number;
  totalApprovedSen: number;
  remainingLimitSen: number | null;
  limitStatus: PanelBalanceStatus;
}

/** What the browser saved: the API answers the export with the CSV file. */
export interface PanelClaimsHistoryExport {
  fileName: string;
  rowCount: number;
}

export interface PanelClaimsHistoryExportInput {
  patientId?: string;
  query?: string;
}

export const panelHistoryKeys = {
  all: ['panel-history'] as const,
  claims: (patientId: string | undefined) =>
    ['panel-history', patientId, 'claims'] as const,
  balances: (patientId: string | undefined) =>
    ['panel-history', patientId, 'balances'] as const,
  overrides: (patientId: string | undefined) =>
    ['panel-history', patientId, 'overrides', 'active'] as const,
  financeSearch: (query: string) =>
    ['panel-history', 'finance-search', query] as const,
};

export function usePatientClaimHistory(patientId: string | undefined) {
  return useQuery({
    queryKey: panelHistoryKeys.claims(patientId),
    queryFn: ({ signal }) =>
      api
        .get<PatientPanelClaim[]>(
          `/patients/${patientId}/panel-history/claims`,
          { signal },
        )
        .then((response) => response.data),
    enabled: !!patientId,
  });
}

export function usePatientPanelBalances(patientId: string | undefined) {
  return useQuery({
    queryKey: panelHistoryKeys.balances(patientId),
    queryFn: ({ signal }) =>
      api
        .get<PatientPanelBalance[]>(
          `/patients/${patientId}/panel-history/balances`,
          { signal },
        )
        .then((response) => response.data),
    enabled: !!patientId,
  });
}

export function useActivePanelOverrides(patientId: string | undefined) {
  return useQuery({
    queryKey: panelHistoryKeys.overrides(patientId),
    queryFn: ({ signal }) =>
      api
        .get<PatientPanelOverride[]>(
          `/patients/${patientId}/panel-history/overrides`,
          { signal, query: { active: true } },
        )
        .then((response) => response.data),
    enabled: !!patientId,
  });
}

export function useFinancePatientPanelSearch(query: string) {
  const normalized = query.trim();
  return useQuery<FinancePatientPanelResult[]>({
    queryKey: panelHistoryKeys.financeSearch(normalized),
    queryFn: ({ signal }) =>
      api
        .get<FinancePatientPanelResult[]>('/finance/panel-history/patients', {
          signal,
          query: { q: normalized },
        })
        .then((response) => response.data),
    enabled: normalized.length >= 2,
    placeholderData: keepPreviousData,
  });
}

/**
 * POST is intentional: creating an export records the actor, filters and time
 * in the API audit log instead of exposing an unaudited download URL. The
 * response is the CSV (text/csv, header row first), saved straight away.
 */
export function useExportPanelClaimsHistory() {
  return useMutation({
    mutationFn: async (
      input: PanelClaimsHistoryExportInput,
    ): Promise<PanelClaimsHistoryExport> => {
      const { data } = await api.post<Blob>(
        '/finance/panel-history/export',
        input,
        { responseType: 'blob' },
      );
      const fileName = `panel-claims-history-${clinicDate()}.csv`;
      saveBlob(data, fileName);
      const lines = (await data.text()).split(/\r?\n/).filter(Boolean);
      return { fileName, rowCount: Math.max(lines.length - 1, 0) };
    },
  });
}

export function toPatientPanelClaimSummary(
  claim: PatientPanelClaim,
): PatientPanelClaimSummary {
  const { approvedAmountSen: _approvedAmountSen, ...summary } = claim;
  return summary;
}

export function toPatientPanelOverrideSummary(
  override: PatientPanelOverride,
): PatientPanelOverrideSummary {
  const {
    approvedAmountSen: _approvedAmountSen,
    reason: _reason,
    recordedByName: _recordedByName,
    ...summary
  } = override;
  return summary;
}

/** Defense in depth if a search response accidentally contains an unmasked IC. */
export function maskedIdentityLabel(identityLabel: string): string {
  const separator = identityLabel.indexOf('·');
  if (separator < 0) return identityLabel;
  const prefix = identityLabel.slice(0, separator + 1).trimEnd();
  const identity = identityLabel.slice(separator + 1).trim();
  const digits = identity.replace(/\D/g, '');
  if (digits.length !== 12) return identityLabel;
  return `${prefix} ••••••-••-${digits.slice(-4)}`;
}
